/**
 * Everything that floats over whatever view is underneath.
 *
 * This exists because the two views drifted. `app.tsx` mounted the composer,
 * the suggestion sheet, the shortcut sheet and the outbox after its early
 * return for a detached message window — so a message window mounted none of
 * them, and had since the day windows shipped. Pressing Reply in one set
 * `composeOpen` and drew nothing. So did `c`, so did `Shift+R`, and so did `?`,
 * because the keyboard handler is global and does not care which window it is
 * in.
 *
 * Nothing about that was visible: no error, no console message, a button that
 * simply did not respond. It was found by somebody pressing Reply.
 *
 * One component, used by both, so the question "which overlays does this view
 * have" has one answer. Adding one here reaches every view that can raise it,
 * which is the property that was missing rather than any particular fix.
 *
 * `InstallHint` is deliberately **not** here. It offers to install the app, and
 * a window showing one message is not where anybody decides to do that — it is
 * the one piece of chrome that genuinely belongs to the main view alone.
 */

import * as nav from "../lib/navigation";
import * as store from "../lib/store";
import { Compose } from "./Compose";
import { Help } from "./Help";
import { Outbox } from "./Outbox";
import { Suggestions } from "./Suggestions";
import { Toasts } from "./Toasts";

/**
 * `composer: false` is for the window whose whole body *is* the composer.
 *
 * Every route into writing a message sets `composeOpen` -- that is how the
 * browser raises its layer -- so in a compose window the flag is inevitably
 * true, and rendering the layer here put a second, corner-pinned composer on
 * top of the one filling the window. Both draw the same draft signal, so at
 * the window's opening size the two coincided almost exactly and nothing
 * looked wrong; maximizing grew the real one and left the impostor floating
 * in the corner. The flag itself stays true on purpose: the global keyboard
 * handler reads it to keep single-key shortcuts out of a composer.
 */
export function Overlays({ composer = true }: { composer?: boolean }) {
  return (
    <>
      {composer && store.composeOpen.value && <Compose />}
      {nav.suggestionsOpen.value && (
        <Suggestions onClose={() => (nav.suggestionsOpen.value = false)} />
      )}
      {nav.helpOpen.value && <Help />}
      {/*
        Outside the shell and outside every view, because a message in flight
        has to stay visible while the person who sent it goes somewhere else --
        which, now that the composer closes on handover, is what they always do.
        It draws nothing while the outbox is empty.
      */}
      <Outbox />
      <Toasts />
    </>
  );
}
